"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { useEarlyBirdCountdown, useEarlyBirdVisible } from "@/lib/earlyBird";

/**
 * Urgency colour scheme: derived from the board accent (`primary`), never a
 * third hue — white-on-primary when sitting on an accent background, primary-
 * on-white on light surfaces. The final-72h "critical" state switches to red,
 * which reads as urgency on any board accent.
 */

function TimeBlock({
  value,
  label,
  ready,
  critical,
  size,
  onDark,
}: {
  value: number;
  label: string;
  ready: boolean;
  critical: boolean;
  size: "sm" | "lg";
  onDark: boolean;
}) {
  return (
    <div className="flex flex-col items-center">
      <span
        className={cn(
          "rounded-lg font-bold tabular-nums",
          size === "lg" ? "px-3 py-2 text-2xl sm:text-3xl" : "px-2 py-1 text-lg",
          critical
            ? "bg-red-600 text-white shadow-[0_0_14px_rgba(220,38,38,0.7)]"
            : onDark
              ? "bg-white text-primary shadow-[0_0_12px_rgba(255,255,255,0.45)]"
              : "bg-primary text-white shadow-sm",
        )}
      >
        {ready ? String(value).padStart(2, "0") : "--"}
      </span>
      <span
        className={cn(
          "mt-1 font-semibold uppercase tracking-wide",
          size === "lg" ? "text-[11px]" : "text-[10px]",
          critical ? (onDark ? "text-red-200" : "text-red-500") : onDark ? "text-white/80" : "text-primary",
        )}
      >
        {label}
      </span>
    </div>
  );
}

/** The live DD:HH:MM:SS blocks. Renders nothing once the deadline passes. */
export function EarlyBirdCountdown({
  size = "sm",
  className,
  onDark = false,
}: {
  size?: "sm" | "lg";
  className?: string;
  /** Set when rendering on an accent (primary) background. */
  onDark?: boolean;
}) {
  const t = useEarlyBirdCountdown();
  if (t.expired) return null;

  const colon = cn(
    "pb-4 font-bold animate-pulse",
    t.critical ? (onDark ? "text-red-200" : "text-red-500") : onDark ? "text-white/70" : "text-primary/70",
  );

  return (
    <div className={cn("flex items-center gap-2", className)} role="timer" aria-label="Early-bird offer countdown">
      <TimeBlock value={t.days} label="Days" ready={t.ready} critical={t.critical} size={size} onDark={onDark} />
      <span className={colon}>:</span>
      <TimeBlock value={t.hours} label="Hrs" ready={t.ready} critical={t.critical} size={size} onDark={onDark} />
      <span className={colon}>:</span>
      <TimeBlock value={t.minutes} label="Min" ready={t.ready} critical={t.critical} size={size} onDark={onDark} />
      <span className={colon}>:</span>
      <TimeBlock value={t.seconds} label="Sec" ready={t.ready} critical={t.critical} size={size} onDark={onDark} />
    </div>
  );
}

/** Pulsing "40% OFF" pill — the flashing bait element, shared across surfaces. */
export function EarlyBirdFlash({ className, onDark = false }: { className?: string; onDark?: boolean }) {
  const t = useEarlyBirdCountdown();
  if (t.expired) return null;
  return (
    <span
      className={cn(
        "animate-pulse rounded-full px-2.5 py-1 text-xs font-black uppercase tracking-wider",
        t.critical
          ? "bg-red-600 text-white shadow-[0_0_14px_rgba(220,38,38,0.8)]"
          : onDark
            ? "bg-white text-primary shadow-[0_0_12px_rgba(255,255,255,0.5)]"
            : "bg-primary text-white shadow-sm",
        className,
      )}
    >
      {t.critical ? "Final hours" : "40% off"}
    </span>
  );
}

/** Compact inline timer for tight strips: [41d][18h][18m][05s]. */
function CompactCountdown({ className }: { className?: string }) {
  const t = useEarlyBirdCountdown();
  if (t.expired) return null;
  const chip = cn(
    "rounded-md px-1.5 py-0.5 text-xs font-bold tabular-nums",
    t.critical ? "bg-red-600 text-white" : "bg-white text-primary",
  );
  const parts: Array<[number, string]> = [
    [t.days, "d"],
    [t.hours, "h"],
    [t.minutes, "m"],
    [t.seconds, "s"],
  ];
  return (
    <span className={cn("inline-flex items-center gap-1", className)} role="timer" aria-label="Early-bird offer countdown">
      {parts.map(([value, unit]) => (
        <span key={unit} className={chip}>
          {t.ready ? String(value).padStart(2, "0") : "--"}
          {unit}
        </span>
      ))}
    </span>
  );
}

function BannerStrip() {
  const t = useEarlyBirdCountdown();

  return (
    <div className="mx-auto flex h-10 max-w-6xl items-center justify-center gap-3 px-4">
      <div className="flex min-w-0 items-center gap-2.5">
        <span
          className={cn(
            "shrink-0 animate-pulse rounded-full px-2 py-0.5 text-[11px] font-black uppercase tracking-wider",
            t.critical ? "bg-red-600 text-white" : "bg-white text-primary",
          )}
        >
          {t.critical ? "Final hours" : "40% off"}
        </span>
        <p className="truncate text-sm font-semibold text-white">
          {/* Short copy on phones — the full line truncates to nothing useful
              next to the badge and CTA at 375px. */}
          <span className="sm:hidden">{t.critical ? "Ends in hours" : "Ends 15 Aug"}</span>
          <span className="hidden sm:inline">
            {t.critical ? "Early-bird pricing ends in hours" : "Early-bird ends 15 Aug, 9am"}
          </span>
          <span className="hidden font-normal text-white/75 md:inline"> — £90 lifetime becomes £150</span>
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <CompactCountdown className="hidden sm:inline-flex" />
        <Link
          href="/pricing"
          className={cn(
            "shrink-0 rounded-full px-3.5 py-1.5 text-xs font-bold transition-transform hover:-translate-y-0.5",
            t.critical ? "bg-red-600 text-white hover:bg-red-500" : "bg-white text-primary hover:bg-white/90",
          )}
        >
          Lock in £90
        </Link>
      </div>
    </div>
  );
}

/**
 * Slim one-line offer strip, permanently anchored to the very top of the
 * viewport (h-10). The navbar sticks 40px below it — pages rendering this
 * banner pass `topClass="top-10"` to `Header`.
 */
export function EarlyBirdBanner() {
  const visible = useEarlyBirdVisible();
  if (!visible) return null;

  return (
    // z-30 keeps the strip above page content but below all z-50 app chrome
    // (dashboard header, mobile search modal, sheets, popovers) so overlays
    // are never painted over by the banner.
    <section className="sticky top-0 z-30 w-full bg-primary shadow-md">
      <BannerStrip />
    </section>
  );
}

/**
 * Site-wide banner rendered from the root layout. Skips /dashboard, whose
 * fixed-height shell renders its own EarlyBirdBanner inside its flex column
 * (a banner above an h-screen container would clip the bottom 40px), and
 * /percentile (the Percentile Labs site — reached at this path in dev; on
 * percentilelabs.com the root layout doesn't render this at all, since the
 * middleware rewrite keeps the browser pathname at "/").
 */
export function GlobalEarlyBirdBanner() {
  const pathname = usePathname();
  if (pathname?.startsWith("/dashboard") || pathname?.startsWith("/percentile")) return null;
  return <EarlyBirdBanner />;
}
